import request from '@/utils/request'

/**
 * 获取所有船舶类型
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     typeName: string,
 *     typeCode: string,
 *     description: string,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 船舶类型列表
 */
export function getAllVesselTypes() {
  return request({
    url: '/vessel/types',
    method: 'get'
  })
}

/**
 * 获取船舶列表（带分页和筛选）
 * @param {Object} params 查询参数
 * @param {number} [params.pageNum=1] 页码
 * @param {number} [params.pageSize=10] 每页条数
 * @param {string} [params.mmsi] 船舶MMSI号
 * @param {string} [params.vesselName] 船舶名称(支持中英文)
 * @param {string} [params.vesselType] 船舶类型
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       mmsi: string,
 *       vesselName: string,
 *       cnName: string,
 *       vesselType: string,
 *       callSign: string,
 *       imo: string,
 *       flag: string,
 *       length: number,
 *       width: number,
 *       depth: number,
 *       load: number,
 *       grossTonnage: string,
 *       buildYear: string,
 *       contacts: string,
 *       contactNumber: string,
 *       remark: string,
 *       createTime: string,
 *       updateTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的船舶列表
 */
export function getVesselList(params) {
  return request({
    url: '/vessel/list',
    method: 'get',
    params
  })
}

/**
 * 获取所有船舶信息
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     mmsi: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     callSign: string,
 *     imo: string,
 *     flag: string,
 *     length: number,
 *     width: number,
 *     depth: number,
 *     load: number,
 *     grossTonnage: string,
 *     buildYear: string,
 *     contacts: string,
 *     contactNumber: string,
 *     remark: string,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 所有船舶列表
 */
export function getAllVessels() {
  return request({
    url: '/vessel',
    method: 'get'
  })
}

/**
 * 根据ID获取船舶详情
 * @param {number} id 船舶ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     mmsi: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     callSign: string,
 *     imo: string,
 *     flag: string,
 *     length: number,
 *     width: number,
 *     depth: number,
 *     load: number,
 *     grossTonnage: string,
 *     buildYear: string,
 *     contacts: string,
 *     contactNumber: string,
 *     remark: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 船舶详情
 */
export function getVesselById(id) {
  return request({
    url: `/vessel/${id}`,
    method: 'get'
  })
}

/**
 * 根据MMSI号获取船舶详情
 * @param {string} mmsi 船舶MMSI号，9位数字
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     mmsi: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     callSign: string,
 *     imo: string,
 *     flag: string,
 *     length: number,
 *     width: number,
 *     depth: number,
 *     load: number,
 *     grossTonnage: string,
 *     buildYear: string,
 *     contacts: string,
 *     contactNumber: string,
 *     remark: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 船舶详情
 */
export function getVesselByMmsi(mmsi) {
  return request({
    url: `/vessel/mmsi/${mmsi}`,
    method: 'get'
  })
}

/**
 * 创建船舶
 * @param {Object} data 船舶数据
 * @param {string} data.mmsi 船舶MMSI号，9位数字
 * @param {string} [data.vesselName] 船舶名称
 * @param {string} [data.cnName] 船舶中文名称
 * @param {string} [data.vesselType] 船舶类型
 * @param {string} [data.callSign] 船舶呼号
 * @param {string} [data.imo] IMO编号，格式：IMO+7位数字
 * @param {string} [data.flag] 船旗国
 * @param {number} [data.length] 船舶长度(米)
 * @param {number} [data.width] 船舶宽度(米)
 * @param {number} [data.depth] 船舶吃水深度(米)
 * @param {number} [data.load] 船舶载重量(吨)
 * @param {string} [data.grossTonnage] 总吨位
 * @param {string} [data.buildYear] 建造年份
 * @param {string} [data.contacts] 联系人姓名
 * @param {string} [data.contactNumber] 联系电话
 * @param {string} [data.remark] 备注信息
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     mmsi: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     callSign: string,
 *     imo: string,
 *     flag: string,
 *     length: number,
 *     width: number,
 *     depth: number,
 *     load: number,
 *     grossTonnage: string,
 *     buildYear: string,
 *     contacts: string,
 *     contactNumber: string,
 *     remark: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的船舶信息
 */
export function createVessel(data) {
  return request({
    url: '/vessel',
    method: 'post',
    data
  })
}

/**
 * 更新船舶
 * @param {number} id 船舶ID
 * @param {Object} data 船舶数据
 * @param {string} data.mmsi 船舶MMSI号，9位数字
 * @param {string} [data.vesselName] 船舶名称
 * @param {string} [data.cnName] 船舶中文名称
 * @param {string} [data.vesselType] 船舶类型
 * @param {string} [data.callSign] 船舶呼号
 * @param {string} [data.imo] IMO编号，格式：IMO+7位数字
 * @param {string} [data.flag] 船旗国
 * @param {number} [data.length] 船舶长度(米)
 * @param {number} [data.width] 船舶宽度(米)
 * @param {number} [data.depth] 船舶吃水深度(米)
 * @param {number} [data.load] 船舶载重量(吨)
 * @param {string} [data.grossTonnage] 总吨位
 * @param {string} [data.buildYear] 建造年份
 * @param {string} [data.contacts] 联系人姓名
 * @param {string} [data.contactNumber] 联系电话
 * @param {string} [data.remark] 备注信息
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     mmsi: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     callSign: string,
 *     imo: string,
 *     flag: string,
 *     length: number,
 *     width: number,
 *     depth: number,
 *     load: number,
 *     grossTonnage: string,
 *     buildYear: string,
 *     contacts: string,
 *     contactNumber: string,
 *     remark: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的船舶信息
 */
export function updateVessel(id, data) {
  return request({
    url: `/vessel/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除船舶
 * @param {number} id 船舶ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteVessel(id) {
  return request({
    url: `/vessel/${id}`,
    method: 'delete'
  })
} 